import { isReduceMotionEnabled } from "@Utils/accessibility";
import { forceMoveFlyingFocus } from "@Utils/flyingFocus";
import { RefObject } from "react";

const DEFAULT_SCROLL_TIME = 600; // milliseconds
const MINIMAL_SCROLL_TIME = 200; // milliseconds

export const scrollElementToViewport = (
  element: HTMLLIElement,
  padding = 0,
  keyboardNavigationRef?: RefObject<boolean>,
) => {
  const container = element.parentElement;
  if (!container) {
    return;
  }
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  let scrollAmount = 0;
  if (elementRect.right > containerRect.right) {
    // Element is partially or fully to the right of the viewport
    scrollAmount = elementRect.right - containerRect.right + padding; // Add some padding to see a fraction of the next day
  } else if (elementRect.left < containerRect.left) {
    scrollAmount = elementRect.left - containerRect.left - padding; // Subtract some padding to see a fraction of the previous day
  }

  if (scrollAmount !== 0) {
    container.scrollTo({
      left: container.scrollLeft + scrollAmount,
      behavior: "smooth",
    });
    container.addEventListener(
      "scrollend",
      () => {
        if (keyboardNavigationRef?.current) {
          forceMoveFlyingFocus();
          keyboardNavigationRef.current = false;
        }
      },
      { once: true },
    );
  }
};

export const animateScrollToPosition = (
  element: HTMLElement,
  targetPosition: number,
  animate = true,
) => {
  if (isReduceMotionEnabled() || !animate) {
    element.scrollLeft = targetPosition;
    return;
  }

  const currentPosition = element.scrollLeft;
  const distance = Math.abs(currentPosition - targetPosition);

  // Make the time needed depend on the covered distance. This makes
  // sure that longer distances take longer to scroll over than short
  // distances. But it should take at least 200ms
  // Taken from CMS: https://github.com/mmz-srf/ezpublish-community/blob/7b3523f96500041075722bc1892d16462f4d6573/src/SRF/CMSSiteBundle/Resources/assets/frontend-components/frontend/modules/containers/swipeable-area/swipeable-area.js#L525-L543
  let duration = DEFAULT_SCROLL_TIME;
  duration = (duration / 1200) * distance;
  duration = Math.max(duration, MINIMAL_SCROLL_TIME);

  let startTime: number | null = null;

  const step = (currentTime: number) => {
    if (startTime === null) {
      startTime = currentTime;
    }
    const elapsedTime = currentTime - startTime;

    if (elapsedTime < duration) {
      const progress = easeInOutSine(elapsedTime / duration);
      const newPosition =
        currentPosition + (targetPosition - currentPosition) * progress;
      element.scrollLeft = newPosition;
      requestAnimationFrame(step);
    } else {
      element.scrollLeft = targetPosition;
    }

    // Update data-scroll-position attribute to keep track of the scroll position for E2E tests
    element.dataset.scrollPosition = element.scrollLeft.toString();
  };

  element.style.scrollBehavior = "auto";
  requestAnimationFrame(step);
};

// Easing function for smooth animation
// Source: https://easings.net/#easeInOutSine
const easeInOutSine = (t: number): number => {
  return -(Math.cos(Math.PI * t) - 1) / 2;
};
